import React, { useContext, useState, useEffect, useRef } from "react";
import { Input, Form, Select, Typography, Row, Col } from "antd";
import API_KEY from "../../../../../../api/index";
import { getConvertedValue, sanitizeNAN } from "utils/helpers";
const EditableContext = React.createContext(null);

const { Text } = Typography;
const { Option } = Select;
export const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};
export const defaultOptions = {
  price: {
    currency: ["USD", "INR", "GBP", "EUR", "RMB", "AUD"],
    unit: [
      "per-piece",
      "per-feet",
      "per-inch",
      "per-cm",
      "per-metre",
      "per-1000-metres",
      "per-gram",
      "per-kilogram",
      "per-metric-ton",
      "per sq. feet",
      "per sq. metre",
    ],
  },
};

export const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  type,
  saving,
  setSaving,
  defaultValue,
  variantData,
  collectionProductId,
  isSizeChanged,
  setSizeChanged,
  isPriceChanged,
  setPriceChanged,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (isSizeChanged && type == "size") {
      handleSave(record);
      patchAndPostAttributes(record);
      setSizeChanged(false);
    }
  }, [isSizeChanged]);

  useEffect(() => {
    if (isPriceChanged && type == "price") {
      handleSave(record);
      patchAndPostAttributes(record);
      setPriceChanged(false);
    }
  }, [isPriceChanged]);

  useEffect(() => {
    if (record && dataIndex) {
      if (!editing) {
        if (dataIndex && record) {
          switch (type) {
            case "size":
              if (dataIndex.split("_").length > 1) {
                let parent = dataIndex.split("_")[0];
                form.setFieldsValue({
                  [`${parent}_length`]: record[parent]?.length,
                  [`${parent}_width`]: record[parent]?.width,
                  [`${parent}_height`]: record[parent]?.height,
                  [dataIndex]: record[dataIndex],
                });
              } else {
                form.setFieldsValue({
                  [`${dataIndex}_length`]: record[dataIndex]?.length,
                  [`${dataIndex}_width`]: record[dataIndex]?.width,
                  [`${dataIndex}_height`]: record[dataIndex]?.height,
                  // [`${dataIndex}_unit`]: record[dataIndex]?.unit,
                });
              }
              if (isSizeChanged) {
                setSizeChanged(false);
              }
              break;
            case "price":
              form.setFieldsValue({ [dataIndex]: record[dataIndex] });
              break;
            case "csv":
              form.setFieldsValue({
                [dataIndex]:
                  typeof record[dataIndex] === "string"
                    ? record[dataIndex]
                      ? record[dataIndex].split(",")
                      : []
                    : record[dataIndex],
              });
              break;
            default:
              if (dataIndex) {
                form.setFieldsValue({
                  [dataIndex]: record[dataIndex],
                });
              }
              break;
          }
        }
      }
    }
  }, [dataIndex, editing, isSizeChanged]);

  const updateFormFields = (values) => {
    if (dataIndex && values) {
      switch (type) {
        case "size":
          form.setFieldsValue({
            [`${dataIndex}_length`]: values[dataIndex]?.length,
            [`${dataIndex}_width`]: values[dataIndex]?.width,
            [`${dataIndex}_height`]: values[dataIndex]?.height,
            [`${dataIndex}_unit`]: record[`${dataIndex}_unit`],
          });
          break;
        case "csv":
          form.setFieldsValue({
            [dataIndex]:
              typeof values[dataIndex] === "string"
                ? values[dataIndex]
                  ? values[dataIndex].split(",")
                  : []
                : values[dataIndex],
          });
          break;
        default:
          if (dataIndex) {
            form.setFieldsValue({
              [dataIndex]: values[dataIndex],
            });
          }
          break;
      }
    }
  };
  const toggleEdit = () => {
    setEditing(!editing);
    // form.setFieldsValue({
    //   [dataIndex]: record[dataIndex],
    // });
  };
  const patchAndPostAttributes = (values) => {
    let keyIndex = dataIndex;
    let splitKeys = dataIndex.split("_");
    let isComposite = splitKeys.length > 1;
    let compositeValue = {};
    let attributeValue = "";
    let computedSize = "";
    let hasValues = true;
    if (isComposite) {
      keyIndex = splitKeys[0];
    }
    let editedValue = values[keyIndex];
    const currentAttribute =
      values.productData?.filter((item) => item.name === keyIndex)[0] || {};

    const { attribute_id = "", attribute_value_id = "" } = currentAttribute;

    switch (type) {
      case "csv":
        attributeValue = editedValue.filter(Boolean).join(",");
        break;
      case "text":
        attributeValue = editedValue;
        break;
      case "price":
        attributeValue = isComposite ? values[keyIndex] : values[dataIndex];
        compositeValue = {
          currency: values[`${keyIndex}_currency`] || "",
          unit: values[`${keyIndex}_unit`] || "",
        };
        break;
      case "size":
        compositeValue = {
          length: editedValue.length || "",
          width: editedValue.width || "",
          height: editedValue.height || "",
          unit: values[`${keyIndex}_unit`] || "",
        };
        const { length, width, height } = compositeValue;
        hasValues = [length, width, height].filter(Boolean).length;

        if (!hasValues && !attribute_value_id) {
          return;
        }
        computedSize = `${[
          compositeValue.length,
          compositeValue.width,
          compositeValue.height,
        ]
          .filter(Boolean)
          .join("x")} ${values[`${keyIndex}_unit`] || ""}`.trim();
        attributeValue = computedSize;
        break;
      default:
        attributeValue = editedValue;
    }

    let requestPayloadForPatch = {
      id: attribute_value_id,
      attribute_id: attribute_id,
      values: attributeValue,
      collections_products_id: values?.product_id,
      composite: compositeValue,
    };
    let requestPayloadForPost = {
      collections_products_id: values?.product_id,
      attribute_values_id: {
        attribute_id: attribute_id,
        values: attributeValue,
        composite: compositeValue,
      },
    };
    if (attribute_value_id && hasValues) {
      setSaving(true);
      API_KEY.URL.patch(
        `${API_KEY.path.attribute_values}/${attribute_value_id}`,
        requestPayloadForPatch
      )
        .then((res) => {
          updateFormFields(values);
          setSaving(false);
        })
        .catch((err) => {
          setSaving(false);
        });
    } else {
      if (hasValues) {
        setSaving(true);
        API_KEY.URL.post(
          API_KEY.path.collections_variant_attribute_values,
          requestPayloadForPost
        )
          .then((res) => {
            setSaving(false);
            // setAttrValueId(res?.data?.data?.attribute_values_id);
          })
          .catch((err) => {
            setSaving(false);
          });
      }
    }
  };

  const saveComposite = async (value, key, compositeKey) => {
    try {
      let newValues = {},
        allValues = {};
      let splitKey = key.split("_");
      let parentKey = "";
      if (splitKey.length > 1) {
        parentKey = splitKey[0];
      }
      if (parentKey) {
        let oldValues = record[parentKey];
        let oldUnit = record[key];
        if (compositeKey === "unit" && oldUnit && oldUnit !== value) {
          Object.keys(oldValues).map(function (current, index) {
            oldValues[current] = getConvertedValue(oldValues[current], value);
          });
        }
        // let convertedValues = Object.keys(oldValues).reduce((acc, item) => {
        //   // return Object.assign(
        //   //   acc,
        //   //   { item: getConvertedValue(oldValues[item], value) },
        //   //   {}
        //   // );
        // });
        newValues = { ...oldValues, [compositeKey]: value };

        allValues = { ...record, [parentKey]: newValues, [key]: value };
      } else {
        let oldValues = record[key];
        newValues = {
          ...oldValues,
          [compositeKey]: value,
        };
        allValues = { ...record, [key]: newValues };
      }
      toggleEdit();

      handleSave(allValues);
      patchAndPostAttributes(allValues);
    } catch (errInfo) {
      console.log("Composite save failed:", errInfo);
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      const valueToUpdate = values[dataIndex];
      toggleEdit();
      const newValues = { ...record, [dataIndex]: valueToUpdate };
      handleSave(newValues);
      patchAndPostAttributes(newValues);
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };
  const getCompositeValue = (values) => {
    const dimensionMap = {
      length: "L",
      width: "B",
      height: "H",
    };
    let size = {
      length: values[1],
      width: values[2],
      height: values[3],
    };

    let unit = values[4] || "";

    const { length = "", width = "", height = "" } = size;
    let sizeKeys = Object.keys(size);
    const validSizes = [length, width, height].filter(Boolean) || [];
    const validKeys = sizeKeys.filter((item) => size[item] && item !== "unit");
    const dimensions = validKeys.map((item) => dimensionMap[item]) || [];

    return `${validSizes.join("x")} ${unit} (${dimensions.join("x")})`;
  };

  const renderFields = (type) => {
    const isComposite = dataIndex.split("_").length > 1;
    // const defaultOptions = isComposite
    //   ? getDefaultOptions(type, dataIndex.split("_")[0])
    //   : [];

    const compositeObj = {
      price: {
        currency: (
          <Form.Item
            style={{
              margin: 0,
            }}
            // onClick={toggleEdit}
            name={dataIndex}
          >
            <Select
              ref={inputRef}
              // onBlur={save}
              // onPressEnter={save}
              onChange={save}
              // defaultValue={record[dataIndex]}
            >
              {defaultOptions.price.currency.map(
                (item, index) =>
                  item && (
                    <Option key={`${index}${item}`} value={item}>
                      {item}
                    </Option>
                  )
              )}
            </Select>
          </Form.Item>
        ),
        unit: (
          <Form.Item
            style={{
              margin: 0,
            }}
            // onClick={toggleEdit}
            name={dataIndex}
          >
            <Select
              ref={inputRef}
              // onBlur={save}
              // onPressEnter={save}
              onChange={save}
              // defaultValue={record[dataIndex]}
            >
              {defaultOptions.price.unit.map(
                (item, index) =>
                  item && (
                    <Option key={`${index}${item}`} value={item}>
                      {item}
                    </Option>
                  )
              )}
            </Select>
          </Form.Item>
        ),
      },
      size: {
        unit: (
          <Form.Item
            style={{
              margin: 0,
            }}
            // onClick={toggleEdit}
            name={dataIndex}
          >
            <Select
              ref={inputRef}
              onPressEnter={(value) => {
                saveComposite(value, dataIndex, "unit");
              }}
              onChange={(value) => {
                saveComposite(value, dataIndex, "unit");
              }}
            >
              {["cms", "inches"].map(
                (item, index) =>
                  item && (
                    <Option key={`${index}${item}`} value={item}>
                      {item}
                    </Option>
                  )
              )}
            </Select>
          </Form.Item>
        ),
      },
    };
    // if (type == "size") debugger;
    const componentsObj = {
      text: (
        <Form.Item
          style={{
            margin: 0,
          }}
          // onClick={toggleEdit}
          name={dataIndex}
        >
          <Input ref={inputRef} onBlur={save} onPressEnter={save} />
        </Form.Item>
      ),
      csv: (
        <Form.Item
          style={{
            margin: 0,
          }}
          // onClick={toggleEdit}
          name={dataIndex}
        >
          <Select
            mode="tags"
            ref={inputRef}
            onBlur={save}
            onPressEnter={save}
            onChange={save}
            notFoundContent={
              <Text type="secondary">Start typing to add...</Text>
            }
          >
            {/* {defaults?.[dataIndex]?.map(
              (item, index) =>
                item && (
                  <Option key={`${index}${item}`} value={item}>
                    {item}
                  </Option>
                )
            )} */}
          </Select>
        </Form.Item>
      ),
      price: (
        <Form.Item
          style={{ marginBottom: "0" }}
          name={dataIndex}
          onClick={toggleEdit}
        >
          <Input
            ref={inputRef}
            onBlur={save}
            onPressEnter={save}
            value={record[dataIndex]}
          />
        </Form.Item>
      ),
      size: (
        <Row
          ref={inputRef}
          style={{ maxHeight: "32px" }}
          justify="space-between"
          gutter={[2]}
        >
          <Col>
            <Form.Item
              name={`${dataIndex}_length`}
              style={{ width: "55px" }}
              // onClick={toggleEdit}
              // ref={inputRef}
            >
              <Input
                onBlur={(e) => {
                  saveComposite(e.target.value, dataIndex, "length");
                }}
                type="number"
                onPressEnter={(e) => {
                  saveComposite(e.target.value, dataIndex, "length");
                }}
              />
            </Form.Item>
          </Col>
          <Col style={{ marginTop: "6px" }}>X</Col>
          <Col>
            <Form.Item
              name={`${dataIndex}_width`}
              style={{ width: "60px" }}
              // onClick={toggleEdit}
            >
              <Input
                // ref={inputRef}
                onBlur={(e) => {
                  saveComposite(e.target.value, dataIndex, "width");
                }}
                type="number"
                onPressEnter={(e) => {
                  saveComposite(e.target.value, dataIndex, "width");
                }}
              />
            </Form.Item>
          </Col>
          <Col style={{ marginTop: "6px" }}>X</Col>
          <Col>
            <Form.Item
              name={`${dataIndex}_height`}
              style={{ width: "60px" }}
              // onClick={toggleEdit}
              // ref={inputRef}
            >
              <Input
                onBlur={(e) => {
                  saveComposite(e.target.value, dataIndex, "height");
                }}
                type="number"
                onPressEnter={(e) => {
                  saveComposite(e.target.value, dataIndex, "height");
                }}
                // addonAfter={
                //   <div style={{ display: "none" }} ref={inputRef}></div>
                // }
              />
            </Form.Item>
          </Col>
        </Row>
      ),
      textarea: (
        <Form.Item name={dataIndex}>
          <Input.TextArea ref={inputRef} onBlur={save} onPressEnter={save} />
        </Form.Item>
      ),
      // select: (
      //   <Select size="large" onChange={save}>
      //     {defaultValues && defaultValues?.select?.length > 0
      //       ? defaultValues?.select?.map((item, index) => (
      //           <Option key={`${index}${item}`} value={item}>
      //             {item}{" "}
      //           </Option>
      //         ))
      //       : null}
      //   </Select>
      // ),
    };
    return isComposite
      ? compositeObj[type][dataIndex.split("_")[1]]
      : componentsObj[type] || (
          <Input ref={inputRef} onBlur={save} onPressEnter={save} />
        );
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <div
      // style={{
      //   margin: 0,
      // }}
      // name={dataIndex}
      // rules={[
      //   {
      //     required: false,
      //     message: `${title} is required.`,
      //   },
      // ]}
      >
        {renderFields(type)}
        {/* <Input ref={inputRef} onPressEnter={save} onBlur={save} /> */}
      </div>
    ) : (
      <div
        // className="editable-cell-value-wrap"
        // style={{
        // //   paddingRight: 24,
        // }}
        onClick={toggleEdit}
      >
        {renderFields(type)}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};
